import { IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { cn } from 'utils/style-utils';
import { CronoSelect, CronoMenuItem } from 'crono-fe-common/components/Select';
import IconInformationS from 'crono-fe-common/icons/Icon-Information-Small';
import IconRightArrow from 'crono-fe-common/icons/Icon-Right-Arrow';
import { colors } from 'crono-fe-common/theme';
import {
  CommunicationStyle,
  CommunicationStyleValues,
} from 'crono-fe-common/types/enums/communicationStyle';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import LoadingAISuggestions from '../loadingAISuggestions';
import SingleOptionMultiplePropertyAiAssistant from '../singleOptionMultiplePropertyAiAssistant';
import useGenerateStrategyProductBenefits from 'hooks/services/assistant/useGetStrategyProductBenefits';
import useGenerateStrategyTargetAudienceChallenges from 'hooks/services/assistant/useGetStrategyTargetAudienceChallenges';
import useCompanyInfoWithFallback from 'hooks/services/findNew/useCompanyInfoWithFallback';
import useGetTemplateVariables from 'hooks/services/variables/useGetTemplateVariables';
import { useAuth } from 'context/auth';
import { AiAssistantFormWrapper } from './style';
import LanguagePicker from 'components/LanguagePicker';
import { Button, RefreshSmIcon } from '@crono-one/design-system';

type AIAssistantStep = 1 | 2 | 3 | 4 | 5;

export interface IPropsAiAssistantForm {
  productDescription: string;
  setProductDescription: (value: string) => void;
  audience: string;
  setAudience: (value: string) => void;
  challenges: string[];
  setChallenges: (value: string[]) => void;
  hows: string[];
  setHows: (value: string[]) => void;
  language: string;
  setLanguage: (value: string) => void;
  toneOfVoice: CommunicationStyle;
  setToneOfVoice: (value: CommunicationStyle) => void;
  styleProps?: React.CSSProperties;
  showFifthStep?: boolean;
  isEditView?: boolean;
}

const AiAssistantForm = ({
  productDescription,
  setProductDescription,
  audience,
  setAudience,
  challenges,
  setChallenges,
  hows,
  setHows,
  language,
  setLanguage,
  toneOfVoice,
  setToneOfVoice,
  styleProps,
  showFifthStep = true,
  isEditView = false,
}: IPropsAiAssistantForm) => {
  const { user } = useAuth();

  const [currentChallenge, setCurrentChallenge] = useState<string>('');
  const [currentHow, setCurrentHow] = useState<string>('');

  const {
    mutateAsync: generateStrategyTargetAudienceChallenges,
    isLoading: isLoadingGenerateStrategyTargetAudienceChallenges,
  } = useGenerateStrategyTargetAudienceChallenges();

  const {
    mutateAsync: generateStrategyProductBenefits,
    isLoading: isLoadingGenerateStrategyProductBenefits,
  } = useGenerateStrategyProductBenefits();

  const [aiAssistantStepToExecute, setAiAssistantStepToExecute] =
    useState<AIAssistantStep | null>(1);

  const clickNext = () => {
    setAiAssistantStepToExecute((prev) => {
      switch (prev) {
        case 1:
          return 2;
        case 2:
          return 3;
        case 3:
          return 4;
        case 4:
          if (showFifthStep) return 5;
          return 4;
        case 5:
        default:
          return 1;
      }
    });
  };

  const focusStep = useCallback(
    (stepToSelect: AIAssistantStep) => {
      if (aiAssistantStepToExecute === stepToSelect) {
        return;
      }
      setAiAssistantStepToExecute(stepToSelect);
    },
    [aiAssistantStepToExecute],
  );

  const step3DivRef = useRef<HTMLDivElement>(null);

  const loadSuggestionsTargetAudienceChallenges = useCallback(async () => {
    const res = await generateStrategyTargetAudienceChallenges({
      productDescription: productDescription,
      targetAudience: audience,
    });
    if (!res.data.data) return;
    setChallenges(res.data.data);
    step3DivRef.current?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });
  }, [
    productDescription,
    audience,
    generateStrategyTargetAudienceChallenges,
    step3DivRef,
  ]);

  const step4DivRef = useRef<HTMLDivElement>(null);

  const loadSuggestionsProductBenefits = useCallback(async () => {
    const res = await generateStrategyProductBenefits({
      productDescription: productDescription,
      targetAudience: audience,
      targetAudienceChallenges: challenges,
    });
    if (!res.data.data) return;
    setHows(res.data.data);
    step4DivRef.current?.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
    });
  }, [
    productDescription,
    audience,
    challenges,
    generateStrategyProductBenefits,
    step4DivRef,
  ]);

  useEffect(() => {
    if (aiAssistantStepToExecute === 3) {
      if (challenges.length === 0) {
        loadSuggestionsTargetAudienceChallenges();
      }
    } else if (aiAssistantStepToExecute === 4) {
      if (hows.length === 0) {
        loadSuggestionsProductBenefits();
      }
    }
  }, [aiAssistantStepToExecute, loadSuggestionsTargetAudienceChallenges]);

  //Load product description from userPreferences or linkedin if present
  const { data: templateVariables } = useGetTemplateVariables();
  const { call: getCompanyInfo } = useCompanyInfoWithFallback();
  useEffect(() => {
    if (!templateVariables || isEditView) return;
    const handle = async () => {
      const productDescriptionUser =
        templateVariables.data.data.productDescription;
      if (productDescriptionUser && productDescriptionUser !== '') {
        setProductDescription(productDescriptionUser);
        return;
      }
      //Get from linkedin if not found
      const linkedinCompanyInfo = await getCompanyInfo({
        url: user?.company?.linkedinUrl,
      });
      if (linkedinCompanyInfo?.company?.description) {
        setProductDescription(linkedinCompanyInfo.company.description);
      }
    };
    handle();
  }, [templateVariables, isEditView]);

  const currentChallengeRef = useRef<HTMLInputElement>(null);

  const addChallenge = () => {
    setChallenges([...challenges, currentChallenge]);
    setCurrentChallenge('');
    setTimeout(() => {
      if (currentChallengeRef.current) {
        currentChallengeRef.current.focus();
      }
    }, 0);
  };

  const currentHowRef = useRef<HTMLInputElement>(null);

  const addHow = () => {
    setHows([...hows, currentHow]);
    setCurrentHow('');
    setTimeout(() => {
      if (currentHowRef.current) {
        currentHowRef.current.focus();
      }
    }, 0);
  };

  return (
    <AiAssistantFormWrapper style={styleProps}>
      <div className="ai-assistant-form__section">
        <div className="ai-assistant-step-title-box">
          <Typography
            fontSize={14}
            lineHeight={'18px'}
            fontWeight={500}
            className={cn('ai-assistant-step-title', {
              disabled: aiAssistantStepToExecute !== 1,
            })}
            onClick={() => focusStep(1)}
          >
            1. What are you selling?
          </Typography>
          <Tooltip
            title="Define a comprehensive product description. By default this is already taken from your template variables or from your LinkedIn company page."
            enterDelay={400}
            enterNextDelay={400}
            arrow
          >
            <div className="icon-information-wrapper">
              <IconInformationS className="icon-information" />
            </div>
          </Tooltip>
        </div>

        {aiAssistantStepToExecute === 1 ? (
          <div className="text-container-wrapper">
            <TextField
              multiline
              minRows={4}
              maxRows={6}
              placeholder="Product description"
              value={productDescription}
              onChange={(e) => setProductDescription(e.target.value)}
              autoFocus
              onFocus={(e) => {
                const value = e.target.value;
                e.target.value = '';
                e.target.value = value;
              }}
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              fullWidth
              onKeyUp={(e) => {
                if (e.key === 'Enter' && productDescription?.trim()) {
                  clickNext();
                }
              }}
            />
            <Button
              tone="primary"
              size="sm"
              className="w-fit self-end min-w-20"
              onClick={clickNext}
              disabled={!productDescription?.trim()}
            >
              Next
            </Button>
          </div>
        ) : (
          !!productDescription && (
            <div
              className="ai-assistant-step-content-not-selected"
              onClick={() => focusStep(1)}
            >
              {productDescription}
            </div>
          )
        )}
      </div>
      <div className="ai-assistant-form__section">
        <div className="ai-assistant-step-title-box">
          <Typography
            fontSize={14}
            lineHeight={'18px'}
            fontWeight={500}
            className={cn('ai-assistant-step-title', {
              disabled: aiAssistantStepToExecute !== 2,
            })}
            onClick={() => focusStep(2)}
          >
            2. Who is your target audience?
          </Typography>

          <Tooltip
            title="Define your buyer persona."
            enterDelay={400}
            enterNextDelay={400}
            arrow
          >
            <div className="icon-information-wrapper">
              <IconInformationS className="icon-information" />
            </div>
          </Tooltip>
        </div>

        {aiAssistantStepToExecute === 2 ? (
          <div className="text-container-wrapper row">
            <TextField
              placeholder="Head of sales"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              autoFocus
              onFocus={(e) => {
                const value = e.target.value;
                e.target.value = '';
                e.target.value = value;
              }}
              fullWidth
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              onKeyUp={(e) => {
                if (e.key === 'Enter' && audience?.trim()) {
                  clickNext();
                }
              }}
            />
            <Button
              tone="primary"
              size="sm"
              className="w-fit self-end min-w-20"
              onClick={clickNext}
              disabled={!audience?.trim()}
            >
              Next
            </Button>
          </div>
        ) : (
          !!audience && (
            <div
              className="ai-assistant-step-content-not-selected"
              onClick={() => focusStep(2)}
            >
              {audience}
            </div>
          )
        )}
      </div>
      <div className="ai-assistant-form__section">
        <div className="ai-assistant-step-title-box">
          <Typography
            fontSize={14}
            lineHeight={'18px'}
            fontWeight={500}
            className={cn('ai-assistant-step-title', {
              disabled: aiAssistantStepToExecute !== 3,
            })}
            onClick={() => focusStep(3)}
          >
            3. Which are their main challenges that you could solve?
          </Typography>

          <Tooltip
            title="Define the main pain points of your target audience. By default, Crono AI will suggest 3 challenges based on previous inputs."
            enterDelay={400}
            enterNextDelay={400}
            arrow
          >
            <div className="icon-information-wrapper">
              <IconInformationS className="icon-information" />
            </div>
          </Tooltip>
        </div>

        {(aiAssistantStepToExecute === 3 || !!challenges.length) && (
          <div
            className={cn('wrapper-suggestions-options', {
              disabled:
                isLoadingGenerateStrategyTargetAudienceChallenges &&
                aiAssistantStepToExecute === 3,
            })}
            ref={step3DivRef}
          >
            <div className="text-container-wrapper__side-next">
              {aiAssistantStepToExecute === 3 ? (
                <TextField
                  placeholder="Industry challenge"
                  value={currentChallenge}
                  ref={currentChallengeRef}
                  onChange={(e) => setCurrentChallenge(e.target.value)}
                  autoFocus
                  onFocus={(e) => {
                    const value = e.target.value;
                    e.target.value = '';
                    e.target.value = value;
                    step3DivRef.current?.scrollIntoView({
                      block: 'center',
                      behavior: 'smooth',
                    });
                  }}
                  fullWidth
                  inputProps={{
                    style: {
                      paddingBlock: 14,
                    },
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter' && currentChallenge) {
                      addChallenge();
                    }
                  }}
                />
              ) : (
                <div
                  className="ai-assistant-step-content-not-selected disabled"
                  onClick={() => focusStep(3)}
                >
                  Industry challenges
                </div>
              )}
              <IconButton
                onClick={() => {
                  if (aiAssistantStepToExecute === 3) {
                    addChallenge();
                  } else {
                    focusStep(3);
                  }
                }}
                className={cn('next-icon-button', {
                  disabled: aiAssistantStepToExecute !== 3 || !currentChallenge,
                })}
              >
                <IconRightArrow
                  className="icon-right-arrow"
                  color={colors.white}
                />
              </IconButton>
            </div>

            {isLoadingGenerateStrategyTargetAudienceChallenges &&
            aiAssistantStepToExecute === 3 ? (
              <LoadingAISuggestions />
            ) : (
              challenges.map((challenge, index) => {
                return (
                  <SingleOptionMultiplePropertyAiAssistant
                    key={index}
                    value={challenge}
                    remove={() => {
                      setChallenges(challenges.filter((_, i) => i !== index));
                    }}
                    editOption={(newValue) => {
                      setChallenges(
                        challenges.map((c, i) => (i === index ? newValue : c)),
                      );
                    }}
                  />
                );
              })
            )}
            {aiAssistantStepToExecute === 3 && (
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="w-fit min-w-20"
                  onClick={loadSuggestionsTargetAudienceChallenges}
                  loading={isLoadingGenerateStrategyTargetAudienceChallenges}
                  startIcon={RefreshSmIcon}
                >
                  Regenerate
                </Button>

                <Button
                  tone="primary"
                  size="sm"
                  className="w-fit min-w-20"
                  onClick={clickNext}
                  disabled={isLoadingGenerateStrategyTargetAudienceChallenges}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="ai-assistant-form__section">
        <div className="ai-assistant-step-title-box">
          <Typography
            fontSize={14}
            lineHeight={'18px'}
            fontWeight={500}
            className={cn('ai-assistant-step-title', {
              disabled: aiAssistantStepToExecute !== 4,
            })}
            onClick={() => focusStep(4)}
          >
            4. Which benefits can you bring?
          </Typography>
          <Tooltip
            title="Define the main product benefits that can help your target audience solve their challenges.By default, Crono AI will suggest 3 product benefits based on previous inputs."
            enterDelay={400}
            enterNextDelay={400}
            arrow
          >
            <div className="icon-information-wrapper">
              <IconInformationS className="icon-information" />
            </div>
          </Tooltip>
        </div>

        {(aiAssistantStepToExecute === 4 || !!hows.length) && (
          <div
            className={cn('wrapper-suggestions-options', {
              disabled:
                isLoadingGenerateStrategyProductBenefits &&
                aiAssistantStepToExecute === 4,
            })}
            ref={step4DivRef}
          >
            <div className="text-container-wrapper__side-next">
              {aiAssistantStepToExecute === 4 ? (
                <TextField
                  placeholder="Product description"
                  value={currentHow}
                  onChange={(e) => setCurrentHow(e.target.value)}
                  ref={currentHowRef}
                  autoFocus
                  onFocus={(e) => {
                    const value = e.target.value;
                    e.target.value = '';
                    e.target.value = value;
                    step4DivRef.current?.scrollIntoView({
                      block: 'center',
                      behavior: 'smooth',
                    });
                  }}
                  fullWidth
                  inputProps={{
                    style: {
                      paddingBlock: 14,
                    },
                  }}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter' && currentHow) {
                      addHow();
                    }
                  }}
                />
              ) : (
                <div
                  className="ai-assistant-step-content-not-selected disabled"
                  onClick={() => focusStep(4)}
                >
                  Product benefits
                </div>
              )}
              <IconButton
                onClick={() => {
                  if (aiAssistantStepToExecute === 4) {
                    addHow();
                  } else {
                    focusStep(4);
                  }
                }}
                className={cn('next-icon-button', {
                  disabled: aiAssistantStepToExecute !== 4 || !currentHow,
                })}
              >
                <IconRightArrow
                  className="icon-right-arrow"
                  color={colors.white}
                />
              </IconButton>
            </div>

            {isLoadingGenerateStrategyProductBenefits &&
            aiAssistantStepToExecute === 4 ? (
              <LoadingAISuggestions />
            ) : (
              hows.map((how, index) => {
                return (
                  <SingleOptionMultiplePropertyAiAssistant
                    key={index}
                    value={how}
                    remove={() => {
                      setHows(hows.filter((_, i) => i !== index));
                    }}
                    editOption={(newValue) => {
                      setHows(hows.map((c, i) => (i === index ? newValue : c)));
                    }}
                  />
                );
              })
            )}
            {aiAssistantStepToExecute === 4 && showFifthStep && (
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'flex-end',
                  gap: 8,
                }}
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="w-fit min-w-20"
                  onClick={loadSuggestionsProductBenefits}
                  startIcon={RefreshSmIcon}
                  loading={isLoadingGenerateStrategyProductBenefits}
                >
                  Regenerate
                </Button>

                <Button
                  tone="primary"
                  size="sm"
                  className="w-fit min-w-20"
                  onClick={clickNext}
                  disabled={isLoadingGenerateStrategyProductBenefits}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {showFifthStep && (
        <div className="ai-assistant-form__section">
          <div className="ai-assistant-step-title-box">
            <Typography
              fontSize={14}
              lineHeight={'18px'}
              fontWeight={500}
              className={cn('ai-assistant-step-title', {
                disabled: aiAssistantStepToExecute !== 5,
              })}
              onClick={() => focusStep(5)}
            >
              5. Language & Tone of Voice?
            </Typography>
            <Tooltip
              title="Define the language and the tone of voice for this sequence."
              enterDelay={400}
              enterNextDelay={400}
              arrow
            >
              <div className="icon-information-wrapper">
                <IconInformationS className="icon-information" />
              </div>
            </Tooltip>
          </div>

          {aiAssistantStepToExecute === 5 && (
            <div className="ai-assistant-form__section split">
              <div className="ai-assistant-form__section-half">
                <LanguagePicker
                  language={language}
                  onLanguageChange={setLanguage}
                />
              </div>
              <div className="ai-assistant-form__section-half">
                <CronoSelect
                  componentsProps={{
                    input: {
                      style: {
                        height: 32,
                        padding: 0,
                      },
                    },
                  }}
                  MenuProps={{
                    style: {
                      zIndex: 35100,
                    },
                  }}
                  sx={{
                    height: 40,
                    borderRadius: '8px',
                  }}
                  value={toneOfVoice}
                  onChange={(e) => {
                    setToneOfVoice(e.target.value as CommunicationStyle);
                  }}
                >
                  {CommunicationStyleValues.map((toneOfVoice) => {
                    return (
                      <CronoMenuItem value={toneOfVoice} key={toneOfVoice}>
                        {toneOfVoice}
                      </CronoMenuItem>
                    );
                  })}
                </CronoSelect>
              </div>
            </div>
          )}
        </div>
      )}
    </AiAssistantFormWrapper>
  );
};

export default AiAssistantForm;
